import { unref } from 'vue'
import { showFailToast } from 'vant'

import type { ClientOrderParams } from '@/api/pay'
import type { DevOrderParams } from '@/api/development'

// api
import { genOrderParamsForTest, loginLifePay } from '@/api/development'
import { genOrder } from '@/api/pay'

import { defineStore } from 'pinia'
import { useBankStore, useCardStore, useWebviewStore } from '@/store'

import usePay from '@/hooks/process/usePay'
import useNative from '@/hooks/useNative'

// utils
import { useI18n } from '@/hooks/useI18n'
import { urlSearch2OrderParams } from '@/utils'
import Spinner from '@/components/Spinner'

// constants
import { NEED_SECURITY_AUTHENTICATE, PAY_ENTRANCE } from '@/constant'

export const useAppStore = defineStore({
  id: 'app',
  state: (): AppStoreState => ({
    inited: false,

    appId: localStorage.getItem('appId') || '',

    token: localStorage.getItem('token') || '',
    senceUserId: localStorage.getItem('senceUserId') || '',
    tempKey: localStorage.getItem('tempKey') || '',
    SDKKey: 'sdf59s4df-/sdf',

    sessionId: '',
    entrance: '',
    deviceId: '',
    channel: '',

    tradeNo: '',

    payType: '',
    todo: '',

    hadSetPwd: false,
    historyStackLength: 0,
    isDirectlyEnter: false,
    card: {},
  }),

  getters: {
    isWeBiteApp(): boolean {
      return this.appId === '100' || this.appId === '101'
    },
    getChannel(): string {
      return this.channel ? this.channel : useWebviewStore().isFromMiniProgram ? '6' : '3'
    },
  },

  actions: {
    setIsDirectlyEnter(bl) {
      this.isDirectlyEnter = bl
    },
    setUserInfo(params) {
      params.sessionId && (this.sessionId = params.sessionId)
      params.token && (this.token = params.token)
      params.channel && (this.channel = String(params.channel))
    },
    /**
     * 登录的时候设置初始化参数
     */
    setInitParam(params) {
      console.log('[初始化SDK参数] ', params)

      const webviewStore = useWebviewStore()

      this.inited = true

      this.token = params.token
      params.tempKey && (this.tempKey = params.tempKey)
      params.SDKKey && (this.SDKKey = params.SDKKey)
      this.senceUserId = params.senceUserId
      localStorage.setItem('token', params.token)
      localStorage.setItem('senceUserId', params.senceUserId)

      this.sessionId = params.sessionId
      this.deviceId = params.deviceId

      this.payType = params.payType

      params.channel && (this.channel = String(params.channel))
      params.entrance && (this.entrance = params.entrance)

      this.tradeNo = params.tradeNo

      if (params.preOrder) {
        const preOrder = JSON.parse(params.preOrder)
        const tradeNo = preOrder.trade_no || preOrder.tradeNo
        this.appId = preOrder?.appId || ''
        localStorage.setItem('appId', this.appId)

        if (tradeNo) {
          this.tradeNo = tradeNo
        } else {
          webviewStore.generateOrderParams = {
            bizContent: preOrder.orderParamStr,
          }
        }
      }

      params.webviewType && (webviewStore.webviewType = params.webviewType)
      params.orderId && (webviewStore.orderId = params.orderId)
      webviewStore.redirectUrl = params.redirect_url
    },

    /** lifePay登录 */
    login() {
      return new Promise(async (resolve, reject) => {
        console.log('[开始进行lifePay登录]')
        const cardStore = useCardStore()
        const bankStore = useBankStore()
        const query: QueryParams = <any>unref(this.$router.currentRoute).query
        const { token, entrance, webviewType, payType } = query
        console.log('[路由参数]', query)

        if (payType) {
          this.payType = payType
          bankStore.updateBankNo()
        }
        console.log('[渠道bankNo] ', bankStore.bankNo)

        if (entrance) sessionStorage.setItem('isHadSetOnBackListener', '0')

        if (!token && this.inited) return resolve(null)

        /** 微信小程序/商城H5登录 从地址中获取参数进行初始化 */
        if (webviewType && NEED_SECURITY_AUTHENTICATE.includes(webviewType)) {
          console.log('[微信小程序/商城H5初始化SDK] ')
          this.setInitParam(urlSearch2OrderParams(query))
          return resolve(null)
        }

        /**
         * 开发环境登录 利用test和uat环境独有的接口进行模拟登录
         * 1. 生成一笔预订单 通过 /test/genPayParam 接口生成订单信息 获取到loginStr和appId以及订单号
         * 2. 登录lifePay 通过订单返回的loginStr和appId进行模拟登录 获取lifePay的登录token
         */
        if ((import.meta.env.DEV || query.lifepayDev) && !query?.noDev) {
          const randomNum = Math.floor(Math.random() * 1000000)
          const sessionId = Math.floor(Math.random() * 100000)

          const params: DevOrderParams = {
            amt: 600, // 支付金额
            // phone: '86-15919208630',
            // phone: '86-12345678901', // 这里一定是86 不能用086 会绑不了卡
            // phone: '86-13247300870', // 这里一定是86 不能用086 会绑不了卡
            phone: '853-65997789',
            // phone: '853-22090600',
            // phone: '853-07040000',
            outTradeNo: randomNum,
            orderSrc: this.getChannel,
            // aomiUserId: '20200317094810004075', // 陆 86-15919208630 银联无感001
            // aomiUserId: '2020082618470300115042', // 853-62777599  无
            // aomiUserId: '20220613113628612846', // 86-13247300870 无
            // aomiUserId: '20180816095125300258', // 86-12345678901 银联无感004
            aomiUserId: '20210707145529290750', // 853-65997789 银联无感0096(设备更换自动解绑了) 中银淘宝卡2346 大西洋账户8119
            // aomiUserId: '20220906110644661630', // 853-22090600
            // aomiUserId: '20220704114629954346', // 853-07040000
          }

          // 生成一笔预订单
          const res = await genOrderParamsForTest(params)
          console.log('[生成一笔预订单] ', res)
          const preOrderData = JSON.parse(res?.result)

          // 登录lifePay
          const loginRes = await loginLifePay({
            sessionId,
            loginStr: preOrderData.loginStr,
            appId: preOrderData.appId,
            channel: 3,
          })
          console.log('[获取到登录lifePay的token] ', loginRes)

          // TODO: lifePay的两个条件 1. 是否初始化 初始化需要什么参数 2. 是否有订单号
          const initParams = {
            sessionId,
            channel: this.channel,
            token: loginRes.result.token,
            todo: 'bindCard',
            entrance: PAY_ENTRANCE.TO_PAY,
            cardId: '6250948200000001',
            deviceId: 'M960BDRA22425',
          }

          console.log('[开发环境进入初始化SDK]')
          this.setInitParam(initParams)

          const genOrderParams = JSON.parse(preOrderData?.preOrderResp?.biz_content)
          this.tradeNo = genOrderParams?.trade_no
          console.log('[从生成的预订单中获取到预订单号] ', this.tradeNo)

          await cardStore.hasSetPwd()

          resolve(initParams)

          return
        }

        /**
         * 澳觅APP
         * 从应用中获取登录的参数进行初始化SDK
         */
        try {
          const loginParams = await useNative().getLoginParams()

          const initParams = {
            ...loginParams,
          }

          console.log('[澳觅APP初始化SDK]', initParams)
          this.setInitParam(initParams)

          resolve(initParams)
        } catch (error) {
          console.log('error: ', error)
          reject(error)
        }
      })
    },

    /** 生成订单号 用于澳觅APP/微信小程序和商城H5 */
    generateOrder() {
      console.log('[进入澳觅APP/微信小程序/商城H5生成预订单方法]')
      const { t } = useI18n()
      const cardStore = useCardStore()
      const { generateOrderParams } = useWebviewStore()

      return new Promise(async (resolve, reject) => {
        try {
          let genOrderParams: ClientOrderParams

          if (generateOrderParams) {
            console.log('[已经存在预订单参数]')
            genOrderParams = generateOrderParams
          } else {
            console.log('[从桥接中获取预订单的参数]')
            const { preOrder } = await useNative().getLoginParams()

            if (!preOrder) {
              showFailToast({
                message: t('app.errMsgOrder'),
                teleport: '#wrapper',
              })

              return reject()
            }
            genOrderParams = JSON.parse(preOrder)
          }
          console.log('generateOrderParams', generateOrderParams)
          console.log('genOrderParams', genOrderParams)
          const res = await genOrder(genOrderParams)
          this.tradeNo = res?.result?.queryPrePayOrderVo?.tradeNo
          console.log('[澳觅APP/微信小程序/商城H5的预订单号]', this.tradeNo)
          await cardStore.hasSetPwd()
          resolve(res)
        } catch (error) {
          console.log('[捕获支付错误] ', error)
          Spinner.close()
          usePay().handlePayError(error)
          reject(error)
        }
      })
    },
  },

  persist: {
    enabled: true,
    // strategies: [
    //   {
    //     key: 'app',
    //     storage: sessionStorage,
    //   },
    //   {
    //     key: 'app',
    //     storage: localStorage,
    //     paths: ['token', 'senceUserId', 'tempKey'],
    //   },
    // ],
  },
})
